import { formatIQD, grandTotal, subtotal } from "./data/price";

// 📐 عرض فاتورة مشروع: حالة الفاتورة المرتبطة + الاعتماد/التحويل لطلب.
// الحالة تنقرأ من الفاتورة نفسها بالباك اند (invoiceStatus) - مو مخزونة بالعرض.
export const invoiceStage = (status) => {
    if (!status || status.missing)
        return { key: "missing", label: "الفاتورة انحذفت", tone: "danger" };
    if (status.orderId)
        return { key: "ordered", label: "تحوّل لطلب ✓", tone: "done" };
    if (status.approved)
        return { key: "approved", label: "معتمد - بانتظار التحويل لطلب", tone: "ok" };
    if (status.stale)
        return { key: "stale", label: "الفاتورة تغيّرت", tone: "warn" };
    return { key: "pending", label: "بانتظار الاعتماد", tone: "pending" };
};

export function InvoicePanel({ link, approving, busy, onApprove, onOpenInvoice }) {
    const stage = invoiceStage(link.invoiceStatus);
    return (<section className="panel invoice-panel">
      <h2>
        فاتورة المشروع
        <small>Invoice</small>
      </h2>
      <div className="invoice-row">
        <span>
          فاتورة رقم <bdi dir="ltr">{link.invoiceStatus?.invoiceNumber || link.invoiceLink.invoiceNumber}</bdi>
        </span>
        <span className={`invoice-badge tone-${stage.tone}`}>{stage.label}</span>
      </div>

      {stage.key === "pending" && (<>
          <p className="hint">
            الصور والأسعار جاية من الفاتورة. اعتمد العرض حتى تنعتمد الفاتورة وينفتح نموذج الطلب، أو اطلع وارجعله
            من «عروض الأسعار» بأي وقت.
          </p>
          <div className="invoice-actions">
            <button type="button" className="btn approve" onClick={onApprove} disabled={approving || busy}>
              {approving ? "جارٍ الاعتماد..." : "✓ اعتماد وتحويل لطلب"}
            </button>
            <button type="button" className="btn ghost" onClick={onOpenInvoice} disabled={approving}>
              تعديل الفاتورة
            </button>
          </div>
        </>)}

      {stage.key === "approved" && (<div className="invoice-actions">
          <button type="button" className="btn approve" onClick={onApprove} disabled={approving || busy}>
            {approving ? "جارٍ التحويل..." : "تحويل لطلب"}
          </button>
        </div>)}

      {stage.key === "stale" && (<>
          <p className="hint">انحفظت نسخة أحدث من الفاتورة. افتح الفاتورة واحفظها حتى يتحدّث العرض، وبعدين اعتمده.</p>
          <div className="invoice-actions">
            <button type="button" className="btn ghost" onClick={onOpenInvoice}>
              فتح الفاتورة
            </button>
          </div>
        </>)}
    </section>);
}

// عرض مرتبط بفاتورة: الأسعار من الفاتورة فقط (للقراءة) - أي تعديل من الفاتورة
// fromQuote: مرحلة التسعيرة (التعديل من «تعديل التسعيرة» بدل الفاتورة)
export function LinkedPriceSummary({ offer, fromQuote }) {
    const count = offer.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    return (<>
      <p className="hint">
        {fromQuote
            ? "الأسعار والرسوم من التسعيرة - أي تعديل عليها يصير من «تعديل التسعيرة»."
            : "الأسعار والرسوم من الفاتورة مباشرة - أي تعديل عليها يصير من «تعديل الفاتورة»."}
      </p>
      <dl className="totals-summary">
        <div>
          <dt>عدد اللوحات</dt>
          <dd dir="ltr">{count}</dd>
        </div>
        <div>
          <dt>المجموع الاولي</dt>
          <dd dir="ltr">{formatIQD(subtotal(offer))} IQD</dd>
        </div>
        <div className="strong">
          <dt>المجموع الكلي</dt>
          <dd dir="ltr">{formatIQD(grandTotal(offer))} IQD</dd>
        </div>
      </dl>
    </>);
}

// 📋 مرحلة العرض للقائمة: تسعيرة، أو حالة الفاتورة المرتبطة، أو يدوي.
// ("rejected" = عروض قديمة من مرحلة التسعيرة الأولى قبل ما تصير فاتورة)
export const proposalStage = (proposal) => {
    if (proposal.stage === "quote")
        return { key: "quote", label: "تسعيرة - بانتظار الاعتماد", tone: "pending" };
    if (proposal.stage === "rejected")
        return { key: "rejected", label: "الزبون رفض", tone: "danger" };
    if (proposal.invoiceLink)
        return invoiceStage(proposal.invoiceStatus);
    return null;
};

// 📋 تسعيرة مشروع (فاتورة "projectQuote"): اعتمادها يحوّل نفس الفاتورة
// لفاتورة مشروع تكمل تفاصيل لوحاتها، وبعدها اعتماد ← طلب مثل أي فاتورة مشروع.
// canManage = بنين/المدير (فاتورة المشروع محصورة بيهم)
export function QuotePanel({ link, canManage, busy, onApprove, onEditQuote }) {
    const stage = proposalStage(link);
    return (<section className="panel invoice-panel">
      <h2>
        تسعيرة المشروع
        <small>Quote</small>
      </h2>
      <div className="invoice-row">
        <span>
          فاتورة رقم <bdi dir="ltr">{link.invoiceStatus?.invoiceNumber || link.invoiceLink.invoiceNumber}</bdi>
        </span>
        <span className={`invoice-badge tone-${stage.tone}`}>{stage.label}</span>
      </div>
      <p className="hint">
        الأسعار من التسعيرة. اعتمادها يحوّلها لفاتورة مشروع (نفس الرقم) تكملين بيها تفاصيل كل لوحة (الشكل، الإطار،
        الصور)، وبعدها تنعتمد وتتحوّل لطلب من هنا.
      </p>
      {canManage ? (<div className="invoice-actions">
          <button type="button" className="btn approve" onClick={onApprove} disabled={busy}>
            ✓ اعتماد التسعيرة ← فاتورة مشروع
          </button>
          <button type="button" className="btn ghost" onClick={onEditQuote} disabled={busy}>
            تعديل التسعيرة
          </button>
        </div>) : (<p className="hint">الاعتماد والتعديل من بنين أو المدير.</p>)}
    </section>);
}
